import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  Button,
  IconButton,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import type { ServiceDetail } from '@/api/services';
import {
  useConfirmService,
  useRejectService,
  useRegisterService,
  useRenewService,
  useCancelService,
  useCloseServicePoc,
  useUpdateServiceEntityName,
} from '@/api/services-mutations';
import { useServiceBillingActions } from './service-billing-actions';
import { DateInput } from '@/components/date-input';

/** Extract a human-readable message from an axios/unknown error. */
function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

type ConfirmKind = 'confirm' | 'register' | 'renew' | null;
type ReasonKind = 'reject' | 'cancel' | 'closePoc' | null;

interface ActionDef {
  key: string;
  label: string;
  tone?: 'primary' | 'secondary' | 'danger';
  onClick: () => void;
}

/**
 * Service lifecycle action bar — status/POC-gated. The two most relevant actions
 * render as buttons; the rest collapse into a "More" dropdown (mirrors the @ui
 * detail-recipe tabActions pattern), so the header stays uncluttered. Confirm-only
 * actions use a `ConfirmDialog`; reason/date actions an inline dialog; rename a
 * text dialog. The backend authorises the transition; the USER runs these live.
 */
export function ServiceActions({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const toast = useToast();
  const status = detail.service_status;

  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [reason, setReason] = useState<ReasonKind>(null);
  const [renameOpen, setRenameOpen] = useState(false);

  const confirmSvc = useConfirmService(id);
  const rejectSvc = useRejectService(id);
  const registerSvc = useRegisterService(id);
  const renewSvc = useRenewService(id);
  const cancelSvc = useCancelService(id);
  const closePoc = useCloseServicePoc(id);
  const rename = useUpdateServiceEntityName(id);
  const billing = useServiceBillingActions(detail);

  const run = (p: Promise<unknown>, ok: string, done: () => void) =>
    p.then(() => { toast.success(ok); done(); }).catch((e) => toast.error(errMsg(e)));

  const isWaitingConfirm = status === 'waiting-confirm';
  const canRegister = isWaitingConfirm || status === 'waiting-create-service';
  const isActive = status === 'active';
  const isDead = status === 'cancelled' || status === 'rejected' || status === 'pending-terminated';

  // Priority-ordered applicable actions: the first two render as buttons, the
  // remainder go under "More".
  const actions: ActionDef[] = [];
  if (isWaitingConfirm) actions.push({ key: 'confirm', label: 'Confirm', tone: 'primary', onClick: () => setConfirmKind('confirm') });
  if (isWaitingConfirm) actions.push({ key: 'reject', label: 'Reject', tone: 'danger', onClick: () => setReason('reject') });
  if (canRegister) actions.push({ key: 'register', label: 'Register', tone: 'secondary', onClick: () => setConfirmKind('register') });
  if (isActive) actions.push({ key: 'renew', label: 'Renew', tone: 'secondary', onClick: () => setConfirmKind('renew') });
  if (detail.is_poc && (isActive || status === 'trialing')) actions.push({ key: 'closePoc', label: 'Close POC', tone: 'secondary', onClick: () => setReason('closePoc') });
  if (isActive) actions.push({ key: 'cancel', label: 'Cancel', tone: 'danger', onClick: () => setReason('cancel') });
  if (!isDead) actions.push({ key: 'rename', label: 'Rename', onClick: () => setRenameOpen(true) });
  // Billing + note actions always live in the overflow menu.
  if (!isDead) for (const b of billing.items) actions.push({ key: b.key, label: b.label, tone: b.tone, onClick: b.onClick });

  if (actions.length === 0) return null;

  const primary = actions.slice(0, 2);
  const overflow = actions.slice(2);

  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
      {primary.map((a) => (
        <Button key={a.key} size="sm" variant={a.tone ?? 'secondary'} onClick={a.onClick}>
          {a.label}
        </Button>
      ))}
      {overflow.length ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton aria-label="More actions" variant="ghost" size="sm">
              <MoreHorizontal size={16} />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflow.map((a) => (
              <DropdownMenuItem key={a.key} tone={a.tone === 'danger' ? 'danger' : undefined} onSelect={a.onClick}>
                {a.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {/* Confirm-only actions */}
      <ConfirmDialog
        open={confirmKind === 'confirm'}
        onOpenChange={(o) => !o && setConfirmKind(null)}
        title="Confirm service"
        description={`Confirm "${detail.service_name}"? This moves it forward in its lifecycle.`}
        confirmLabel="Confirm"
        loading={confirmSvc.isPending}
        onConfirm={() => run(confirmSvc.mutateAsync(), 'Service confirmed', () => setConfirmKind(null))}
      />
      <ConfirmDialog
        open={confirmKind === 'register'}
        onOpenChange={(o) => !o && setConfirmKind(null)}
        title="Register service"
        description={`Provision "${detail.service_name}" now?`}
        confirmLabel="Register"
        loading={registerSvc.isPending}
        onConfirm={() => run(registerSvc.mutateAsync(), 'Service registered', () => setConfirmKind(null))}
      />
      <ConfirmDialog
        open={confirmKind === 'renew'}
        onOpenChange={(o) => !o && setConfirmKind(null)}
        title="Renew service"
        description={`Renew "${detail.service_name}" for the next cycle?`}
        confirmLabel="Renew"
        loading={renewSvc.isPending}
        onConfirm={() => run(renewSvc.mutateAsync(), 'Service renewed', () => setConfirmKind(null))}
      />

      {/* Reason / date dialogs */}
      <ReasonDialog
        kind={reason}
        serviceName={detail.service_name}
        pending={rejectSvc.isPending || cancelSvc.isPending || closePoc.isPending}
        onClose={() => setReason(null)}
        onSubmit={(text, stopDate) => {
          if (reason === 'reject') return run(rejectSvc.mutateAsync(text), 'Service rejected', () => setReason(null));
          if (reason === 'closePoc') return run(closePoc.mutateAsync(text), 'POC closed', () => setReason(null));
          return run(
            cancelSvc.mutateAsync({ service_stopped_date: stopDate || null, service_reason_cancel: text }),
            'Service cancelled',
            () => setReason(null),
          );
        }}
      />

      <RenameDialog
        open={renameOpen}
        current={detail.name ?? ''}
        pending={rename.isPending}
        onClose={() => setRenameOpen(false)}
        onSubmit={(name) => run(rename.mutateAsync(name), 'Entity name updated', () => setRenameOpen(false))}
      />

      {billing.dialogs}
    </div>
  );
}

const REASON_META: Record<Exclude<ReasonKind, null>, { title: string; needsDate: boolean; confirm: string }> = {
  reject: { title: 'Reject service', needsDate: false, confirm: 'Reject' },
  cancel: { title: 'Cancel service', needsDate: true, confirm: 'Cancel service' },
  closePoc: { title: 'Close POC', needsDate: false, confirm: 'Close POC' },
};

function ReasonDialog({
  kind,
  serviceName,
  pending,
  onClose,
  onSubmit,
}: {
  kind: ReasonKind;
  serviceName: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (reason: string, stopDate: string) => void;
}) {
  const [text, setText] = useState('');
  const [stopDate, setStopDate] = useState('');
  if (!kind) return null;
  const meta = REASON_META[kind];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ color: 'var(--vn-fg-muted)', marginTop: 'var(--vn-space-0)' }}>{serviceName}</p>
          {meta.needsDate ? (
            <Field label="Stop date">
              {(p) => <DateInput {...p} value={stopDate} onChange={(e) => setStopDate(e.target.value)} />}
            </Field>
          ) : null}
          <Field label="Reason" required>
            {(p) => <Input {...p} value={text} onChange={(e) => setText(e.target.value)} placeholder="Enter a reason…" />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Close
          </Button>
          <Button variant="danger" disabled={!text.trim() || pending} onClick={() => onSubmit(text.trim(), stopDate)}>
            {meta.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({
  open,
  current,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  current: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(current);
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename service entity</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="Entity name">
            {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder="Service instance name…" autoFocus />}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Close
          </Button>
          <Button variant="primary" disabled={!name.trim() || pending} onClick={() => onSubmit(name.trim())}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
